import { useEffect, useRef, useState } from 'react';
import { cartTotals, credentialFromCart, sessionMeta, vouchersFromCart } from '@ce/core';
import { trackColor } from '@ce/theme';
import { useTheme } from '../ThemeProvider';
import { btn, chip, divider, emptyBox, kicker, portrait, saveStyle, settle, t } from '../styles';
import { Sheen } from '../components/Ambient';
import { Wireframe } from '../components/Wireframe';
import { ThemePicker } from '../components/ThemePicker';
import type { ScreenProps } from './types';

/**
 * Badge code.
 *
 * NOT a real QR encoder — this draws correct QR *geometry* (finder squares,
 * timing rows) filled with a deterministic pattern derived from the badge
 * code, matching the prototype's placeholder. It will not scan.
 *
 * Before a real event, swap `renderQr` for an encoder — `qrcode` on web,
 * `react-native-qrcode-svg` on native — and feed it the credential payload the
 * Convention Engine access-control reader expects. Everything else about this
 * component stays as is.
 */
function BadgeCode({ value, size = 112 }: { value: string; size?: number }) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const modules = renderQr(value);
    const n = modules.length;
    const scale = size / n;

    // Literal colours, NOT theme tokens. Two reasons, and both matter:
    // a reader needs fixed black-on-white contrast whatever look the attendee
    // picked, and Canvas 2D cannot resolve CSS custom properties at all — it
    // silently ignores `var(...)` and falls back to black, which is how this
    // rendered as a solid black square once.
    ctx.fillStyle = '#e9e9ed';
    ctx.fillRect(0, 0, size, size);
    ctx.fillStyle = '#16172a';
    for (let y = 0; y < n; y++) {
      for (let x = 0; x < n; x++) {
        if (modules[y][x]) ctx.fillRect(x * scale, y * scale, scale, scale);
      }
    }
  }, [value, size]);

  return (
    <canvas
      ref={ref}
      width={size}
      height={size}
      aria-label={`Badge code ${value}`}
      style={{
        width: 84, height: 84, borderRadius: 6, flex: 'none',
        // Fixed, not themed — see the note in the draw call above.
        background: '#e9e9ed', border: '3px solid #f3f5fe',
      }}
    />
  );
}

export function Me({ bundle, state, dispatch, config }: ScreenProps) {
  const theme = useTheme();
  const saved = bundle.sessions.filter((s) => state.saved.includes(s.id));

  return (
    <div className="stagger">
      {state.credential ? (
        <div
          style={{
            padding: 16, borderRadius: 12, background: 'var(--color-surface)',
            boxShadow: 'var(--shadow-sm)', display: 'flex', gap: 15, alignItems: 'center',
          }}
        >
          <BadgeCode value={state.credential.code} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={kicker(9.5, 'var(--color-accent-300)', 0.16)}>
              {state.credential.kind}
            </div>
            <div style={{ font: '500 17px/1.2 var(--font-body)', margin: '7px 0 5px' }}>
              {state.credential.holder}
            </div>
            <div style={{ font: '400 11px/1.5 var(--font-mono)', color: t(0.5) }}>
              {state.credential.code}
            </div>
          </div>
        </div>
      ) : (
        <CredentialPrompt bundle={bundle} dispatch={dispatch} canBuy={config.features.tickets} />
      )}

      {config.features.shop && (
      <button
        type="button"
        onClick={() => dispatch({ type: 'go', screen: 'shop' })}
        style={{
          position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center',
          gap: 12, width: '100%', textAlign: 'left', padding: '13px 14px', marginTop: 10,
          borderRadius: 11, cursor: 'pointer', border: `1px solid ${divider}`,
          background: 'var(--color-surface)', color: 'var(--color-text)',
        }}
      >
        <Sheen delay={5} />
        <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
          <div style={{ font: '500 14px/1.2 var(--font-body)' }}>{bundle.strings.merchCta}</div>
          <div style={{ fontSize: 11.5, color: t(0.5), marginTop: 4 }}>
            {(bundle.merch ?? []).length} items · collect on site
          </div>
        </div>
        <div
          style={{
            flex: 'none', width: 22, height: 22, borderRadius: '50%',
            border: '1px solid var(--color-accent)', color: 'var(--color-accent)',
            display: 'grid', placeItems: 'center', font: '500 11px/1 var(--font-body)',
            position: 'relative',
          }}
        >
          ›
        </div>
      </button>
      )}

      <div style={{ ...kicker(), margin: '24px 0 11px' }}>My vouchers</div>
      {state.vouchers.length > 0 ? (
        <div style={{ display: 'grid', gap: 9 }}>
          {state.vouchers.map((v, i) => (
            <div
              key={`${v.code}-${i}`}
              style={{
                display: 'flex', gap: 13, alignItems: 'center', padding: '13px 14px',
                borderRadius: 10, border: '1px solid var(--color-accent-700)',
                background: 'var(--color-accent-900)',
              }}
            >
              <div
                style={{
                  flex: 'none', width: 40, height: 40, borderRadius: 8,
                  // Fixed contrast, not themed: a reader needs black on white
                  // whatever look the attendee picked.
                  background: 'repeating-linear-gradient(45deg, #e9e9ed 0 3px, #16172a 3px 6px)',
                }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ font: '500 14px/1.2 var(--font-body)' }}>{v.title}</div>
                <div style={{ fontSize: 11.5, color: t(0.55), marginTop: 4 }}>{v.meta}</div>
              </div>
              <div style={{ font: '400 10px/1 var(--font-mono)', color: 'var(--color-accent-300)' }}>
                {v.code}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={emptyBox}>{bundle.strings.emptyVouchers}</div>
      )}

      <div style={{ ...kicker(), margin: '24px 0 11px' }}>
        My schedule · {state.saved.length} starred
      </div>
      {saved.length > 0 ? (
        <div style={{ display: 'grid', gap: 8 }}>
          {saved.map((s) => (
            <div
              key={s.id}
              style={{
                display: 'flex', gap: 12, padding: '12px 14px', borderRadius: 10,
                background: 'var(--color-surface)',
              }}
            >
              <div
                style={{
                  width: 2, alignSelf: 'stretch', borderRadius: 2,
                  background: trackColor(theme, s.track), flex: 'none',
                }}
              />
              <button
                type="button"
                onClick={() => dispatch({ type: 'openSession', id: s.id })}
                style={{
                  flex: 1, minWidth: 0, textAlign: 'left', border: 0, padding: 0,
                  background: 'transparent', color: 'var(--color-text)', cursor: 'pointer',
                }}
              >
                <div style={{ font: '500 14px/1.25 var(--font-body)' }}>{s.title}</div>
                <div style={{ fontSize: 11.5, color: t(0.52), marginTop: 4 }}>
                  {sessionMeta(bundle, s)}
                </div>
              </button>
              <button
                type="button"
                aria-label={`Unstar ${s.title}`}
                onClick={() => dispatch({ type: 'toggleSave', id: s.id })}
                style={saveStyle(true)}
              >
                ★
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div style={emptyBox}>{bundle.strings.emptySaved}</div>
      )}

      <div style={{ ...kicker(), margin: '24px 0 11px' }}>My interests</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
        {state.interests.map((label) => (
          <button
            key={label}
            type="button"
            onClick={() => dispatch({ type: 'toggleInterest', label })}
            style={chip(true)}
          >
            {label}
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={() => dispatch({ type: 'restartOnboarding' })}
        style={btn('secondary', { marginTop: 16, height: 42, width: '100%' })}
      >
        Redo interest setup
      </button>

      {config.features.themePicker && <ThemePicker
        bundle={bundle}
        active={state.themeId}
        shuffle={state.themeShuffle}
        onPick={(id) => dispatch({ type: 'setTheme', id })}
        onToggleShuffle={() => dispatch({ type: 'toggleThemeShuffle' })}
      />}
      <div style={{ height: 26, borderColor: divider }} />
    </div>
  );
}

export function Cart({ bundle, state, dispatch }: ScreenProps) {
  const totals = cartTotals(bundle, state.cart);

  if (state.cart.length === 0) {
    return (
      <div className="stagger">
        <div style={emptyBox}>{bundle.strings.emptyCart}</div>
        <button
          type="button"
          onClick={() => dispatch({ type: 'go', screen: 'talent' })}
          style={btn('secondary', { height: 42, marginTop: 12, width: '100%' })}
        >
          Browse talent
        </button>
        <div style={{ height: 26 }} />
      </div>
    );
  }

  return (
    <div className="stagger">
      <div style={{ display: 'grid', gap: 9 }}>
        {state.cart.map((line, i) => {
          const person =
            line.kind === 'booking' ? bundle.talent.find((p) => p.id === line.talentId) : undefined;
          const meta =
            line.kind === 'booking'
              ? `${line.dayLabel} ${line.slot} · ${line.room}`
              : line.kind === 'ticket'
                ? 'Admission · badge issued at checkout'
                : [line.variantLabel, 'collect on site'].filter(Boolean).join(' · ');

          return (
            <div
              key={`${line.title}-${i}`}
              style={{
                display: 'flex', gap: 13, alignItems: 'center', padding: '13px 14px',
                borderRadius: 10, background: 'var(--color-surface)', boxShadow: 'var(--shadow-sm)',
                ...settle(i),
              }}
            >
              <div
                style={{
                  flex: 'none', width: 44, height: 44, borderRadius: 8, overflow: 'hidden',
                  background: 'var(--color-neutral-900)', display: 'grid', placeItems: 'center',
                }}
              >
                {person ? (
                  <div style={{ ...portrait(person.photoUrl, 20) }} />
                ) : (
                  <Wireframe art={line.kind === 'ticket' ? 'badge' : 'exhibitor-hall'} strokeWidth={1.2} />
                )}
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ font: '500 14px/1.2 var(--font-body)' }}>{line.title}</div>
                <div style={{ fontSize: 11.5, color: t(0.52), marginTop: 4 }}>{meta}</div>

                {line.kind !== 'booking' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                    <QtyButton label="Decrease" onClick={() => dispatch({ type: 'setLineQty', index: i, qty: line.qty - 1 })}>–</QtyButton>
                    <span style={{ font: '400 12px/1 var(--font-mono)', minWidth: 14, textAlign: 'center' }}>
                      {line.qty}
                    </span>
                    <QtyButton label="Increase" onClick={() => dispatch({ type: 'setLineQty', index: i, qty: line.qty + 1 })}>+</QtyButton>
                  </div>
                )}
              </div>

              <div style={{ textAlign: 'right', flex: 'none' }}>
                <div style={{ font: '500 14px/1 var(--font-body)' }}>
                  {bundle.event.currency}{line.price * (line.kind === 'booking' ? 1 : line.qty)}
                </div>
                <button
                  type="button"
                  onClick={() => dispatch({ type: 'removeCartItem', index: i })}
                  style={btn('ghost', { fontSize: 11, marginTop: 4 })}
                >
                  Remove
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 16, padding: 15, borderRadius: 10, border: `1px solid ${divider}` }}>
        {totals.lines.map((l) => (
          <div
            key={l.label}
            style={{
              display: 'flex', justifyContent: 'space-between', fontSize: 12.5,
              padding: '4px 0', color: t(0.6),
            }}
          >
            <span>{l.label}</span>
            <span>{l.value}</span>
          </div>
        ))}
        <div style={{ height: 1, background: divider, margin: '9px 0' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', font: '500 17px/1 var(--font-body)' }}>
          <span>Total</span>
          <span>{totals.totalLabel}</span>
        </div>
      </div>

      <button
        type="button"
        onClick={() => {
          // Buying admission is what turns Me from a prompt into a badge.
          const issued = credentialFromCart(bundle, state.cart, state.credential?.holder ?? bundle.badge.holder);
          if (issued && !state.credential) dispatch({ type: 'setCredential', credential: issued });
          dispatch({
            type: 'receiveMessage',
            message: {
              id: `order-${Date.now()}`,
              kind: 'order',
              at: new Date().toISOString(),
              title: 'Order confirmed',
              body: `${state.cart.length} item${state.cart.length === 1 ? '' : 's'}. Vouchers are in Me; merchandise is collected on site.`,
              target: { screen: 'me' },
            },
          });
          dispatch({ type: 'checkout', vouchers: vouchersFromCart(state.cart) });
        }}
        style={btn('primary', {
          height: 48, fontSize: 15, borderRadius: 10, marginTop: 14, width: '100%',
        })}
      >
        Pay {totals.totalLabel}
      </button>
      <div style={{ fontSize: 11, lineHeight: 1.55, color: t(0.4), marginTop: 10, textAlign: 'center' }}>
        {bundle.strings.cartFooter}
      </div>
      <div style={{ height: 26 }} />
    </div>
  );
}

/** Placeholder module grid — see the note on BadgeCode. Deterministic per badge. */
function renderQr(value: string): boolean[][] {
  const n = 29;
  const grid: boolean[][] = Array.from({ length: n }, () => Array<boolean>(n).fill(false));

  const finder = (ox: number, oy: number) => {
    for (let y = 0; y < 7; y++) {
      for (let x = 0; x < 7; x++) {
        const edge = x === 0 || x === 6 || y === 0 || y === 6;
        const core = x >= 2 && x <= 4 && y >= 2 && y <= 4;
        grid[oy + y][ox + x] = edge || core;
      }
    }
  };
  finder(0, 0);
  finder(n - 7, 0);
  finder(0, n - 7);

  // Timing patterns.
  for (let i = 8; i < n - 8; i++) {
    grid[6][i] = i % 2 === 0;
    grid[i][6] = i % 2 === 0;
  }

  // Payload region: a stable hash of the badge code, so the same badge always
  // renders the same code and a scanner test is reproducible.
  let h = 2166136261;
  for (let i = 0; i < value.length; i++) {
    h ^= value.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  let seed = h;
  const rnd = () => {
    seed = (Math.imul(seed, 1103515245) + 12345) & 0x7fffffff;
    return seed / 0x7fffffff;
  };
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      const inFinder =
        (x < 9 && y < 9) || (x > n - 10 && y < 9) || (x < 9 && y > n - 10) || x === 6 || y === 6;
      if (inFinder) continue;
      grid[y][x] = rnd() > 0.5;
    }
  }
  return grid;
}


/** Small +/- control for quantity lines. */
function QtyButton({
  label, onClick, children,
}: {
  label: string; onClick: () => void; children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      style={{
        width: 24, height: 24, borderRadius: 6, cursor: 'pointer',
        border: `1px solid ${divider}`, background: 'transparent',
        color: 'var(--color-text)', font: '500 13px/1 var(--font-body)',
      }}
    >
      {children}
    </button>
  );
}

/**
 * No badge on the device.
 *
 * A first-class state rather than an empty card: someone who has not bought yet
 * is exactly the person the app most needs to help, and someone who bought
 * elsewhere needs a way to bring that badge in.
 */
function CredentialPrompt({
  bundle, dispatch, canBuy,
}: {
  bundle: ScreenProps['bundle']; dispatch: ScreenProps['dispatch']; canBuy: boolean;
}) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');

  const submit = () => {
    const trimmed = code.trim().toUpperCase();
    // Deliberately shallow: real validation belongs on the ticketing system,
    // and pretending to verify here would be worse than not checking.
    if (trimmed.length < 6) {
      setError('That does not look like a badge code.');
      return;
    }
    dispatch({
      type: 'setCredential',
      credential: {
        code: trimmed,
        holder: bundle.badge.holder,
        kind: bundle.badge.kind,
        source: 'entered',
        days: bundle.days.map((d) => d.key),
      },
    });
  };

  return (
    <div
      style={{
        position: 'relative', overflow: 'hidden', padding: 16, borderRadius: 12,
        border: '1px solid var(--color-accent-700)', background: 'var(--color-accent-900)',
      }}
    >
      <Sheen delay={1} />
      <div style={{ position: 'relative' }}>
        <div style={kicker(9.5, 'var(--color-accent-300)', 0.16)}>
          {bundle.strings.credentialPromptTitle}
        </div>
        <div style={{ fontSize: 12.5, lineHeight: 1.6, color: t(0.65), marginTop: 9 }}>
          {bundle.strings.credentialPromptBody}
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <input
            value={code}
            onChange={(e) => { setCode(e.target.value); setError(''); }}
            onKeyDown={(e) => e.key === 'Enter' && submit()}
            placeholder="NC26-4D-00000"
            aria-label="Badge code"
            style={{
              flex: 1, minWidth: 0, height: 42, padding: '0 12px', borderRadius: 9,
              border: `1px solid ${divider}`, background: 'var(--color-surface)',
              color: 'var(--color-text)', font: '400 13px/1 var(--font-mono)', outline: 'none',
            }}
          />
          <button type="button" onClick={submit} style={btn('primary', { height: 42, borderRadius: 9 })}>
            Add
          </button>
        </div>
        {error && (
          <div style={{ fontSize: 11.5, color: 'var(--color-accent-300)', marginTop: 8 }}>{error}</div>
        )}

        {canBuy && (
          <button
            type="button"
            onClick={() => dispatch({ type: 'go', screen: 'tickets' })}
            style={btn('secondary', { width: '100%', height: 42, marginTop: 10 })}
          >
            {bundle.strings.credentialBuy}
          </button>
        )}
      </div>
    </div>
  );
}
